import { enableProdMode, provideZoneChangeDetection } from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';

import { AppComponent } from './app/app.component';
import { appConfig } from './app/app.config';
import { environment } from './environments/environment';

if (environment.production) {
  enableProdMode();
}

interface BufferedLog {
  level: 'warn' | 'error';
  message: string;
  error: Error | null;
  timestamp: string;
}

const PRE_BOOT_BUFFER: BufferedLog[] = [];

function bufferIfPreBoot(
  level: BufferedLog['level'],
  message: string,
  error: Error | null,
): void {
  PRE_BOOT_BUFFER.push({
    level,
    message,
    error,
    timestamp: new Date().toISOString(),
  });
  const tag = level === 'error' ? '[BOOT-ERR]' : '[BOOT-WARN]';

  console[level](`${tag} ${message}`, error);
}

if (typeof window !== 'undefined') {
  window.addEventListener('error', (event: ErrorEvent) => {
    const err =
      event.error instanceof Error
        ? event.error
        : new Error(event.message || 'window.error');

    bufferIfPreBoot('error', 'window.error', err);
  });

  window.addEventListener(
    'unhandledrejection',
    (event: PromiseRejectionEvent) => {
      const reason: unknown = event.reason;
      const err =
        reason instanceof Error
          ? reason
          : new Error(
              typeof reason === 'string' ? reason : 'unhandledrejection',
            );

      bufferIfPreBoot('error', 'unhandledrejection', err);
    },
  );
}

bootstrapApplication(AppComponent, {
  ...appConfig,
  providers: [provideZoneChangeDetection(), ...appConfig.providers],
})
  .then(async (appRef) => {
    if (PRE_BOOT_BUFFER.length === 0) {
      return;
    }
    const { LoggerService } = await import('./app/core/logging');
    const logger = appRef.injector.get(LoggerService);

    for (const buffered of PRE_BOOT_BUFFER) {
      logger[buffered.level](buffered.message, buffered.error ?? undefined, {
        module: 'pre-boot',
      });
    }
    PRE_BOOT_BUFFER.length = 0;
  })
  .catch((err) => console.error('[BOOT-FAIL]', err));
